<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import axios from 'axios'

defineProps({
  show: {
    type: Boolean,
    required: true
  }
})



defineEmits(['close'])

// Active ticket category
const activeCategory = ref('all')

const userSearchQuery = ref('')
// New ticket form
const showNewTicketForm = ref(false)
const newTicket = ref({
  title: '',
  description: '',
  type: 'bug',
  assignee: '',
  importance: 5,
  tags: [],
  folder: null
})

// Replace mock tickets data with server data
const tickets = ref([])

// Add loading and error states
const isLoading = ref(false)
const error = ref(null)

// Add auth state
const token = ref(null)
const isAuthenticated = computed(() => !!token.value)

// Login function
const login = async () => {
  try {
    const response = await axios.post(`https://api.test.oddscomposer.io/auth/login`, {
      username: 'admin',
      password: 'admin123'
    })
    token.value = response.data.token
    // Set token for all future requests
    axios.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
  } catch (err) {
    console.error('Login failed:', err)
    error.value = 'Authentication failed'
  }
}

// Update the API base URL to point to your Laravel backend
const API_BASE_URL = 'https://api.test.oddscomposer.io';

// Update the axios requests to use the new API endpoints
const fetchTickets = async () => {
  // if (!isAuthenticated.value) {
  //   await login();
  // }
  
  isLoading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}/tickets`);
    tickets.value = response.data;
  } catch (err) {
    error.value = 'Failed to load tickets.';
    console.error(err);
  } finally {
    isLoading.value = false;
  }
}

// Add this function after the fetchTickets function
const createTicket = async () => {
  if (newTicket.value.title && newTicket.value.description) {
    try {
      const selectedAssignee = assignees.value.find(a => a.id === newTicket.value.assignee)
      
      const response = await axios.post(`${API_BASE_URL}/tickets`, {
        title: newTicket.value.title,
        description: newTicket.value.description,
        priority: newTicket.value.priority,
        type: newTicket.value.type,
        assignee: selectedAssignee ? {
          id: selectedAssignee.id,
          name: selectedAssignee.name,
          role: selectedAssignee.role,
          avatar: selectedAssignee.avatar
        } : null,
        importance: newTicket.value.importance,
        folder: newTicket.value.folder,
        tags: newTicket.value.tags
      })

      // Add the new ticket to the list
      tickets.value.unshift(response.data)
      
      // Reset form and close modal
      showNewTicketForm.value = false
      newTicket.value = {
        title: '',
        description: '',
        priority: 'medium',
        type: 'bug',
        assignee: '',
        importance: 5,
        tags: [],
        folder: null
      }
    } catch (err) {
      error.value = 'Failed to create ticket.'
      console.error(err)
    }
  }
}

// Add this function to handle comments
const addComment = async () => {
  if (newComment.value.trim() && selectedTicket.value) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/tickets/${selectedTicket.value.id}/comments`,
        {
          text: newComment.value
        }
      )
      
      if (!selectedTicket.value.comments) {
        selectedTicket.value.comments = []
      }
      selectedTicket.value.comments.push(response.data)
      newComment.value = ''
    } catch (err) {
      error.value = 'Failed to add comment.'
      console.error(err)
    }
  }
}

// Filter categories
const categories = [
  { id: 'all', label: 'All Tickets', icon: 'fa-ticket-alt' },
  { id: 'open', label: 'Open', icon: 'fa-exclamation-circle' },
  { id: 'in_progress', label: 'In Progress', icon: 'fa-clock' },
  { id: 'testing', label: 'In Testing', icon: 'fa-vial' },
  { id: 'resolved', label: 'Resolved', icon: 'fa-check-circle' },
  { id: 'closed', label: 'Closed', icon: 'fa-times-circle' }
]

// Priority badges
const priorityBadges = {
  low: 'bg-gray-100 text-gray-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-red-100 text-red-800'
}

// Type badges
const typeBadges = {
  bug: 'bg-red-100 text-red-800',
  feature: 'bg-blue-100 text-blue-800',
  improvement: 'bg-green-100 text-green-800',
  support: 'bg-purple-100 text-purple-800'
}

// Search functionality
const searchQuery = ref('')
const filteredTickets = computed(() => {
  let filtered = tickets.value || []
  
  // Filter by category
  if (activeCategory.value !== 'all') {
    filtered = filtered.filter(ticket => ticket.status === activeCategory.value)
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(ticket => 
      ticket.title?.toLowerCase().includes(query) ||
      ticket.description?.toLowerCase().includes(query) ||
      String(ticket.id).toLowerCase().includes(query)
    )
  }

  // Apply quick filters
  if (activeQuickFilters.value.includes('high_importance')) {
    filtered = filtered.filter(ticket => (ticket.importance || 0) >= 8)
  }

  // Filter by folder
  if (selectedFolder.value) {
    filtered = filtered.filter(ticket => ticket.folder?.id === selectedFolder.value)
  }

  // Filter by tags if any quick filters are active
  if (activeQuickFilters.value.includes('tagged')) {
    filtered = filtered.filter(ticket => ticket.tags?.length > 0)
  }

  // Filter by selected user
  if (selectedUserId.value) {
    filtered = filtered.filter(ticket => 
      ticket.assignee?.id === selectedUserId.value || 
      ticket.reporter?.id === selectedUserId.value
    )
  }

  return filtered
})

// Selected ticket details
const selectedTicket = ref(null)
const newComment = ref('')

// Add chat participants (mock data)
const participants = ref([
  {
    id: 1,
    name: 'Support Team',
    role: 'support',
    status: 'online',
    avatar: null
  },
  {
    id: 2,
    name: 'Development Team',
    role: 'dev',
    status: 'online',
    avatar: null
  }
])

// Function to send chat message
const sendChatMessage = () => {
  if (chatMessage.value.trim()) {
    chatMessages.value.push({
      id: Date.now(),
      text: chatMessage.value,
      sender: 'You',
      time: new Date().toISOString(),
      type: 'user'
    })
    
    chatMessage.value = ''

    setTimeout(() => {
      chatMessages.value.push({
        id: Date.now(),
        text: 'Thank you for your message. Our support team will get back to you shortly.',
        sender: 'Support Team',
        time: new Date().toISOString(),
        type: 'support'
      })
    }, 1000)
  }
}

// Add new testing-related data
const testResults = ref([
  {
    ticketId: 'TCK001',
    tests: [
      {
        id: 1,
        name: 'API Connection Test',
        status: 'passed',
        notes: 'Successfully connected to endpoint',
        timestamp: '2024-03-15T15:30:00'
      },
      {
        id: 2,
        name: 'Data Format Validation',
        status: 'failed',
        notes: 'Incorrect response format',
        timestamp: '2024-03-15T15:35:00'
      }
    ]
  }
])

// Add new function to handle testing phase
const moveToTesting = async () => {
  if (!selectedTicket.value) return
  try {
    await axios.patch(`${API_BASE_URL}/tickets/${selectedTicket.value.id}/status`, { status: 'testing' })
    selectedTicket.value.status = 'testing'
    chatMessages.value.push({
      id: Date.now(),
      sender: 'System',
      text: 'Ticket has been moved to testing phase',
      time: new Date().toLocaleString()
    })
  } catch (err) {
    console.error('Failed to move ticket to testing:', err)
  }
}

// Add function to add test result
const addTestResult = async (ticketId, testResult) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/tickets/${ticketId}/tests`, testResult)
    const ticketTests = testResults.value.find(t => t.ticketId === ticketId)
    if (ticketTests) {
      ticketTests.tests.push(response.data)
    }
  } catch (err) {
    error.value = 'Failed to add test result.'
    console.error(err)
  }
}

// Add to script setup
const showNewTestForm = ref(false)
const newTest = ref({
  name: '',
  status: 'pending',
  notes: ''
})

// Add new function to submit test result
const submitTestResult = () => {
  if (newTest.value.name && selectedTicket.value) {
    addTestResult(selectedTicket.value.id, {
      name: newTest.value.name,
      status: newTest.value.status,
      notes: newTest.value.notes
    })
    
    // Reset form
    newTest.value = {
      name: '',
      status: 'pending',
      notes: ''
    }
    showNewTestForm.value = false

    // If all tests passed, suggest resolving the ticket
    const ticketTests = testResults.value.find(t => t.ticketId === selectedTicket.value.id)
    if (ticketTests && ticketTests.tests.every(test => test.status === 'passed')) {
      // Add a system comment suggesting resolution
      if (selectedTicket.value.comments) {
        selectedTicket.value.comments.push({
          id: Date.now(),
          text: 'All tests have passed. Consider resolving this ticket.',
          user: 'System',
          time: 'Just now'
        })
      }
    }
  }
}

// Add to existing script setup
const viewMode = ref('list') // or 'grid'
const sortBy = ref('newest')
const activeQuickFilters = ref([])

// Quick filters
const quickFilters = [
  { id: 'high_priority', label: 'High Priority', icon: 'fa-exclamation-circle' },
  { id: 'high_importance', label: 'High Importance', icon: 'fa-star' },
  { id: 'my_tickets', label: 'My Tickets', icon: 'fa-user' },
  { id: 'unassigned', label: 'Unassigned', icon: 'fa-user-plus' },
  { id: 'recent', label: 'Recent', icon: 'fa-clock' },
  { id: 'tagged', label: 'Tagged', icon: 'fa-tags' },
  { id: 'untagged', label: 'Untagged', icon: 'fa-tag-slash' }
]

// Toggle quick filter
const toggleQuickFilter = (filterId) => {
  const index = activeQuickFilters.value.indexOf(filterId)
  if (index === -1) {
    activeQuickFilters.value.push(filterId)
  } else {
    activeQuickFilters.value.splice(index, 1)
  }
}

// Enhanced sorting and filtering
const sortedAndFilteredTickets = computed(() => {
  let filtered = filteredTickets.value

  // Apply sorting
  return [...filtered].sort((a, b) => {
    const direction = sortDirection.value === 'asc' ? 1 : -1
    
    switch (sortBy.value) {
      case 'newest':
        return direction * (new Date(b.created) - new Date(a.created))
      case 'oldest':
        return direction * (new Date(a.created) - new Date(b.created))
      case 'priority':
        const priorityOrder = { high: 3, medium: 2, low: 1 }
        return direction * (priorityOrder[b.priority] - priorityOrder[a.priority])
      case 'importance':
        return direction * ((b.importance || 0) - (a.importance || 0))
      case 'status':
        return direction * a.status.localeCompare(b.status)
      default:
        return 0
    }
  })
})

// Calculate testing progress
const getTestingProgress = (ticketId) => {
  const ticketTests = testResults.value.find(t => t.ticketId === ticketId)
  if (!ticketTests || ticketTests.tests.length === 0) return 0
  
  const passedTests = ticketTests.tests.filter(t => t.status === 'passed').length
  return (passedTests / ticketTests.tests.length) * 100
}

// Draggable divider functionality
const isDragging = ref(false)
const ticketListWidth = ref(1500)
const minWidth = 1200
const maxWidth = 2400

const handleDrag = (e) => {
  if (!isDragging.value) return
  
  const newWidth = e.clientX - 320 // 320 is the left sidebar width (w-80)
  if (newWidth >= minWidth && newWidth <= maxWidth) {
    ticketListWidth.value = newWidth
  }
}

// Add event listeners for drag functionality
onMounted(() => {
  // fetchTickets()   // Disabled - no tickets API
  // fetchFolders()   // Disabled - no folders API
  document.addEventListener('mousemove', handleDrag)
  document.addEventListener('mouseup', () => isDragging.value = false)

  // Merge currentTickets into tickets if not already present
  for (const t of currentTickets) {
    if (!tickets.value.find(existing => existing.id === t.id)) {
      tickets.value.push(t)
    }
  }
})

onUnmounted(() => {
  document.removeEventListener('mousemove', handleDrag)
  document.removeEventListener('mouseup', () => isDragging.value = false)
})

// Add these refs near the top of the script setup section, with the other refs
const showChat = ref(false)
const chatMessage = ref('')
const chatMessages = ref([
  {
    id: 1,
    text: 'Welcome! How can we help you today?',
    sender: 'Support Team',
    time: new Date().toISOString(),
    type: 'support'
  }
])

// Add this new function for handling Enter key
const handleChatKeydown = (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    sendChatMessage()
  }
}

// Add this to the script setup section, near other const definitions
const importanceColors = {
  1: 'text-emerald-500',
  2: 'text-emerald-600',
  3: 'text-teal-600',
  4: 'text-sky-600',
  5: 'text-blue-600',
  6: 'text-indigo-600',
  7: 'text-purple-600',
  8: 'text-fuchsia-600',
  9: 'text-pink-600',
  10: 'text-rose-600'
}

// Add this function after addTestResult and before the categories const
const updateImportance = async (newValue) => {
  if (!selectedTicket.value) return
  
  try {
    await axios.patch(`${API_BASE_URL}/tickets/${selectedTicket.value.id}`, {
      importance: parseInt(newValue)
    })
    
    // Update the local ticket data
    selectedTicket.value.importance = parseInt(newValue)
    
    // Update the ticket in the main tickets list
    const ticketIndex = tickets.value.findIndex(t => t.id === selectedTicket.value.id)
    if (ticketIndex !== -1) {
      tickets.value[ticketIndex].importance = parseInt(newValue)
    }
  } catch (err) {
    error.value = 'Failed to update importance level.'
    console.error(err)
  }
}

// Add these refs near the top with other refs
const tags = ref([
  { id: 1, name: 'Frontend', color: 'blue' },
  { id: 2, name: 'Backend', color: 'green' },
  { id: 3, name: 'UI/UX', color: 'purple' },
  { id: 4, name: 'Critical', color: 'red' },
  { id: 5, name: 'Enhancement', color: 'yellow' }
])

const folders = ref([])

const selectedFolder = ref(null)
const showTagsModal = ref(false)
const selectedTags = ref([])

// Add these new functions after other functions
const addTag = async (tagId) => {
  if (!selectedTicket.value) return
  
  try {
    await axios.post(`${API_BASE_URL}/tickets/${selectedTicket.value.id}/tags`, {
      tagId
    })
    
    if (!selectedTicket.value.tags) {
      selectedTicket.value.tags = []
    }
    
    const tag = tags.value.find(t => t.id === tagId)
    if (tag && !selectedTicket.value.tags.find(t => t.id === tagId)) {
      selectedTicket.value.tags.push(tag)
    }
  } catch (err) {
    error.value = 'Failed to add tag.'
    console.error(err)
  }
}

const removeTag = async (tagId) => {
  if (!selectedTicket.value) return
  
  try {
    await axios.delete(`${API_BASE_URL}/tickets/${selectedTicket.value.id}/tags/${tagId}`)
    selectedTicket.value.tags = selectedTicket.value.tags.filter(t => t.id !== tagId)
  } catch (err) {
    error.value = 'Failed to remove tag.'
    console.error(err)
  }
}

const moveToFolder = async (folderId) => {
  if (!selectedTicket.value) return
  
  try {
    await axios.patch(`${API_BASE_URL}/tickets/${selectedTicket.value.id}`, {
      folderId
    })
    selectedTicket.value.folder = folders.value.find(f => f.id === folderId)
  } catch (err) {
    error.value = 'Failed to move ticket to folder.'
    console.error(err)
  }
}

// Add these new refs near the other refs
const showNewFolderForm = ref(false)
const newFolder = ref({
  name: '',
  icon: 'folder',
  description: ''
})

// Add available folder icons
const availableFolderIcons = ref([
  'folder',
  'project-diagram',
  'tasks',
  'bug',
  'code',
  'headset',
  'users',
  'star',
  'rocket'
])

// Add new ref for tracking pending folders
const pendingFolders = ref(new Set())

// Add new ref for tracking failed folders
const failedFolders = ref(new Set())

// Add new ref for tracking folder errors
const folderErrors = ref(new Map())
const showErrorLog = ref(false)
const selectedErrorLog = ref(null)

// Add new ref for popup positioning
const errorLogPosition = ref({ x: 0, y: 0 })

// Modify the createFolder function's catch block
const createFolder = async (tempId = null) => {
  if (!newFolder.value.name) return
  
  // Use existing tempId for retries or create new one
  const folderId = tempId || `temp-${Date.now()}`
  
  // Add the folder immediately to the UI with a pending state
  const newFolderData = {
    id: folderId,
    name: newFolder.value.name,
    icon: newFolder.value.icon,
    description: newFolder.value.description,
    isPending: true
  }
  
  // For retry attempts, update the existing folder data
  const existingIndex = folders.value.findIndex(f => f.id === folderId)
  if (existingIndex !== -1) {
    folders.value[existingIndex] = { ...newFolderData }
  } else {
    folders.value.push(newFolderData)
  }
  
  pendingFolders.value.add(folderId)
  failedFolders.value.delete(folderId)
  showNewFolderForm.value = false
  
  try {
    const response = await axios.post(`${API_BASE_URL}/folders`, {
      name: newFolder.value.name,
      icon: newFolder.value.icon,
      description: newFolder.value.description
    })
    
    // Replace the temporary folder with the real one
    const index = folders.value.findIndex(f => f.id === folderId)
    if (index !== -1) {
      folders.value[index] = response.data
    }
    pendingFolders.value.delete(folderId)
    
    newFolder.value = {
      name: '',
      icon: 'folder',
      description: ''
    }
  } catch (err) {
    pendingFolders.value.delete(folderId)
    failedFolders.value.add(folderId)
    error.value = 'Failed to save folder. You can retry when ready.'
    // Store error details
    folderErrors.value.set(folderId, {
      timestamp: new Date(),
      error: err.response?.data?.message || err.message || 'Unknown error',
      fullError: {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        code: err.code,
        name: err.name,
        config: {
          baseURL: err.config?.baseURL,
          url: err.config?.url,
          method: err.config?.method,
          data: err.config?.data
        },
        stack: err.stack
      },
      request: {
        method: 'POST',
        url: `${API_BASE_URL}/folders`,
        data: {
          name: newFolder.value.name,
          icon: newFolder.value.icon,
          description: newFolder.value.description
        }
      }
    })
    console.error(err)
  }
}

// Modify the retryCreateFolder function
const retryCreateFolder = (folderId) => {
  const failedFolder = folders.value.find(f => f.id === folderId)
  if (failedFolder) {
    createFolder(folderId)
  }
}

// Add new refs for delete confirmation
const showDeleteConfirm = ref(false)
const folderToDelete = ref(null)
const deletePassword = ref('')
const deleteError = ref('')

// Modify the deleteFolder function and add confirmation handling
const confirmDelete = (folderId, event) => {
  event.stopPropagation()
  folderToDelete.value = folderId
  showDeleteConfirm.value = true
  deletePassword.value = ''
  deleteError.value = ''
}

const executeDelete = async () => {
  if (!deletePassword.value) {
    deleteError.value = 'Password is required'
    return
  }

  try {
    // Verify password first
    await axios.post(`${API_BASE_URL}/auth/verify`, {
      password: deletePassword.value
    })

    // If password is correct, proceed with deletion
    await axios.delete(`${API_BASE_URL}/folders/${folderToDelete.value}`)
    folders.value = folders.value.filter(f => f.id !== folderToDelete.value)
    if (selectedFolder.value === folderToDelete.value) {
      selectedFolder.value = null
    }
    
    // Reset and close confirmation
    showDeleteConfirm.value = false
    folderToDelete.value = null
    deletePassword.value = ''
    deleteError.value = ''
  } catch (err) {
    deleteError.value = err.response?.data?.message || 'Invalid password or failed to delete folder'
    console.error(err)
  }
}

// Add this function after createTicket
const quickAddTicketToFolder = (folderId, event) => {
  event.stopPropagation()
  showNewTicketForm.value = true
  // Pre-set the folder in the new ticket form
  newTicket.value = {
    title: '',
    description: '',
    priority: 'medium',
    type: 'bug',
    assignee: '',
    importance: 5,
    tags: [],
    folder: folderId
  }
}

const showFolderSelect = ref(false)

// Add click outside handler to close folder select
const handleClickOutside = (event) => {
  const dropdown = document.querySelector('.folder-select')
  if (dropdown && !dropdown.contains(event.target)) {
    showFolderSelect.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Add near other refs
const folderSearchQuery = ref('')

// Add computed property for filtered folders
const filteredFolders = computed(() => {
  if (!folderSearchQuery.value) return folders.value
  
  const query = folderSearchQuery.value.toLowerCase()
  return folders.value.filter(folder => 
    folder.name.toLowerCase().includes(query) ||
    folder.description?.toLowerCase().includes(query)
  )
})

// Initialize users with demo data
const users = ref([
  {
    id: 1,
    name: 'Alex Developer',
    email: 'alex@company.com',
    isOnline: true,
    role: 'Developer',
    avatar: 'A'
  },
  {
    id: 2,
    name: 'Maria Designer',
    email: 'maria@company.com',
    isOnline: true,
    role: 'Designer',
    avatar: 'M'
  },
  {
    id: 3,
    name: 'Tom QA',
    email: 'tom@company.com',
    isOnline: false,
    role: 'QA',
    avatar: 'T'
  },
  {
    id: 4,
    name: 'Sarah Manager',
    email: 'sarah@company.com',
    isOnline: true,
    role: 'Product Manager',
    avatar: 'S'
  },
  {
    id: 5,
    name: 'James Support',
    email: 'james@company.com',
    isOnline: false,
    role: 'Support',
    avatar: 'J'
  }
])

// Add a computed property for online users count with safety check
const onlineUsersCount = computed(() => {
  return Array.isArray(users.value) ? users.value.filter(u => u?.isOnline).length : 0
})

// Add function to fetch users
const fetchUsers = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/users`)
    users.value = response.data
  } catch (err) {
    console.error('Failed to fetch users:', err)
  }
}

const filteredUsers = computed(() => {
  if (!userSearchQuery.value || !Array.isArray(users.value)) return users.value || []
  const query = userSearchQuery.value.toLowerCase()
  return users.value.filter(user => 
    user?.name?.toLowerCase().includes(query) ||
    user?.email?.toLowerCase().includes(query)
  )
})

// Add new ref for selected user
const selectedUserId = ref(null)

// Add new ref for users menu state
const isUsersMenuExpanded = ref(true)

// Add function to toggle users menu
const toggleUsersMenu = () => {
  isUsersMenuExpanded.value = !isUsersMenuExpanded.value
}

// Add new ref for sorting menu state
const showSortMenu = ref(false)

// Updated sort options with shorter labels
const sortOptions = {
  newest: 'Newest',
  oldest: 'Oldest',
  priority: 'Priority',
  importance: 'Important',
  status: 'Status'
}

// Add click outside handler for sort menu
onMounted(() => {
  document.addEventListener('click', (event) => {
    const sortButton = document.querySelector('.sort-button')
    const sortMenu = document.querySelector('.sort-menu')
    if (!sortButton?.contains(event.target) && !sortMenu?.contains(event.target)) {
      showSortMenu.value = false
    }
  })
})

// Add sort direction state
const sortDirection = ref('desc')

// Update sort function to handle direction
const updateSort = (value) => {
  if (sortBy.value === value) {
    // Toggle direction if clicking same sort option
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortBy.value = value
    // Reset direction for new sort option
    sortDirection.value = value === 'oldest' ? 'asc' : 'desc'
  }
}

// Add to existing script setup section
const editMode = ref(false)
const originalTicket = ref(null) // Store original ticket data

// Add function to handle saving ticket changes
const saveTicket = async () => {
  if (!selectedTicket.value) return

  try {
    // Show loading state
    isLoading.value = true
    
    const response = await axios.patch(
      `${API_BASE_URL}/tickets/${selectedTicket.value.id}`, 
      {
        title: selectedTicket.value.title,
        description: selectedTicket.value.description,
        status: selectedTicket.value.status,
        priority: selectedTicket.value.priority,
        type: selectedTicket.value.type,
        folder: selectedTicket.value.folder,
        tags: selectedTicket.value.tags,
        importance: selectedTicket.value.importance
      }
    )

    // Update the ticket in the main list
    const index = tickets.value.findIndex(t => t.id === selectedTicket.value.id)
    if (index !== -1) {
      tickets.value[index] = { ...tickets.value[index], ...response.data }
    }

    // Update selected ticket with response data
    selectedTicket.value = response.data
    
    // Exit edit mode
    editMode.value = false
    
    // Show success message
    const successMessage = {
      id: Date.now(),
      sender: 'System',
      text: 'Ticket updated successfully',
      time: new Date().toLocaleString(),
      type: 'system'
    }
    chatMessages.value.push(successMessage)

  } catch (err) {
    console.error('Failed to save ticket:', err)
    error.value = 'Failed to save ticket changes.'
    
    // Revert changes on error
    if (originalTicket.value) {
      selectedTicket.value = { ...originalTicket.value }
    }
    
    // Show error in chat
    chatMessages.value.push({
      id: Date.now(),
      sender: 'System',
      text: 'Failed to update ticket: ' + (err.response?.data?.message || err.message),
      time: new Date().toLocaleString(),
      type: 'error'
    })
  } finally {
    isLoading.value = false
  }
}

// Modify the watch for selectedTicket to store original data
watch(
  () => selectedTicket.value?.id,
  (newId) => {
    if (newId) {
      // Store a deep copy of the original ticket when selecting a new ticket
      originalTicket.value = JSON.parse(JSON.stringify(selectedTicket.value))
    }
    // Reset edit mode when switching tickets
    editMode.value = false
  }
)

// Add watcher for edit mode to handle cancellation
watch(
  () => editMode.value,
  (newValue) => {
    if (!newValue && originalTicket.value) {
      // If exiting edit mode without saving, revert changes
      selectedTicket.value = JSON.parse(JSON.stringify(originalTicket.value))
    }
  }
)

// Add watcher for ticket changes in edit mode
watch(
  () => selectedTicket.value,
  async (newVal, oldVal) => {
    if (editMode.value && oldVal && newVal && 
        (newVal.title !== oldVal.title ||
         newVal.description !== oldVal.description ||
         newVal.status !== oldVal.status || 
         newVal.priority !== oldVal.priority || 
         newVal.type !== oldVal.type)) {
      try {
        await axios.patch(`${API_BASE_URL}/tickets/${selectedTicket.value.id}`, {
          title: newVal.title,
          description: newVal.description,
          status: newVal.status,
          priority: newVal.priority,
          type: newVal.type
        })
        // Update the ticket in the main list
        const index = tickets.value.findIndex(t => t.id === selectedTicket.value.id)
        if (index !== -1) {
          tickets.value[index] = { ...tickets.value[index], ...newVal }
        }
      } catch (err) {
        error.value = 'Failed to update ticket.'
        console.error(err)
        // Revert changes on error
        selectedTicket.value = { ...oldVal }
      }
    }
  },
  { deep: true }
)

// Add function to archive ticket
const archiveTicket = async () => {
  if (!selectedTicket.value) return
  
  try {
    await axios.patch(`${API_BASE_URL}/tickets/${selectedTicket.value.id}`, {
      archived: true
    })
    // Remove ticket from list
    tickets.value = tickets.value.filter(t => t.id !== selectedTicket.value.id)
    selectedTicket.value = null
  } catch (err) {
    error.value = 'Failed to archive ticket.'
    console.error(err)
  }
}

// Status badges
const statusBadges = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  testing: 'bg-green-100 text-green-800',
  resolved: 'bg-gray-100 text-gray-800',
  closed: 'bg-gray-100 text-gray-800'
}

// Add new functions for ticket actions
const resolveTicket = async () => {
  if (!selectedTicket.value) return
  try {
    await axios.patch(`${API_BASE_URL}/tickets/${selectedTicket.value.id}/status`, { status: 'resolved' })
    selectedTicket.value.status = 'resolved'
    chatMessages.value.push({
      id: Date.now(),
      sender: 'System',
      text: 'Ticket has been marked as resolved',
      time: new Date().toLocaleString()
    })
  } catch (err) {
    console.error('Failed to resolve ticket:', err)
  }
}

const closeTicket = async () => {
  if (!selectedTicket.value) return
  try {
    await axios.patch(`${API_BASE_URL}/tickets/${selectedTicket.value.id}/status`, { status: 'closed' })
    selectedTicket.value.status = 'closed'
    chatMessages.value.push({
      id: Date.now(),
      sender: 'System',
      text: 'Ticket has been closed',
      time: new Date().toLocaleString()
    })
  } catch (err) {
    console.error('Failed to close ticket:', err)
  }
}

// Add this near the top with other refs
const assignees = ref([
  {
    id: 1,
    name: 'John Developer',
    role: 'Frontend Developer',
    avatar: 'JD',
    status: 'available'
  },
  {
    id: 2,
    name: 'Sarah Engineer',
    role: 'Backend Engineer',
    avatar: 'SE',
    status: 'busy'
  },
  {
    id: 3,
    name: 'Mike Designer',
    role: 'UI/UX Designer',
    avatar: 'MD',
    status: 'available'
  },
  {
    id: 4,
    name: 'Lisa QA',
    role: 'Quality Assurance',
    avatar: 'LQ',
    status: 'offline'
  },
  {
    id: 5,
    name: 'David Manager',
    role: 'Project Manager',
    avatar: 'DM',
    status: 'available'
  }
])

// Add this ref to control assignee dropdown
const showAssigneeSelect = ref(false)

// Add this computed property for filtered assignees
const filteredAssignees = computed(() => {
  if (!userSearchQuery.value) return assignees.value
  
  const query = userSearchQuery.value.toLowerCase()
  return assignees.value.filter(assignee => 
    assignee.name.toLowerCase().includes(query) ||
    assignee.role.toLowerCase().includes(query)
  )
})

// Add this function to handle assignee selection
const selectAssignee = (assignee) => {
  newTicket.value.assignee = assignee.id
  showAssigneeSelect.value = false
}

// Add new function to fetch folders
const fetchFolders = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/folders`)
    folders.value = response.data
  } catch (err) {
    console.error('Failed to fetch folders:', err)
    error.value = 'Failed to load folders.'
  }
}



// Add this function to handle save changes
const handleSaveChanges = async () => {
  if (editMode.value) {
    // If in edit mode, save changes
    try {
      isLoading.value = true
      
      const response = await axios.patch(
        `${API_BASE_URL}/tickets/${selectedTicket.value.id}`,
        {
          title: selectedTicket.value.title,
          description: selectedTicket.value.description,
          status: selectedTicket.value.status,
          priority: selectedTicket.value.priority,
          type: selectedTicket.value.type,
          folder: selectedTicket.value.folder,
          tags: selectedTicket.value.tags,
          importance: selectedTicket.value.importance
        }
      )

      // Update local data
      const index = tickets.value.findIndex(t => t.id === selectedTicket.value.id)
      if (index !== -1) {
        tickets.value[index] = { ...tickets.value[index], ...response.data }
      }
      
      // Exit edit mode
      editMode.value = false
      
      // Show success message
      chatMessages.value.push({
        id: Date.now(),
        sender: 'System',
        text: 'Changes saved successfully',
        time: new Date().toLocaleString(),
        type: 'success'
      })

    } catch (err) {
      console.error('Failed to save changes:', err)
      error.value = 'Failed to save changes: ' + (err.response?.data?.message || err.message)
      
      // Show error message
      chatMessages.value.push({
        id: Date.now(),
        sender: 'System',
        text: 'Failed to save changes: ' + (err.response?.data?.message || err.message),
        time: new Date().toLocaleString(),
        type: 'error'
      })
    } finally {
      isLoading.value = false
    }
  } else {
    // If not in edit mode, enter edit mode
    editMode.value = true
    // Store original ticket data for potential revert
    originalTicket.value = JSON.parse(JSON.stringify(selectedTicket.value))
  }
}

// Add new refs for timeline
const timeView = ref('weeks')
const currentDate = ref(new Date())

// Timeline data
const timelineData = ref([
  {
    user: 'marysmith@constr...',
    tasks: [
      {
        name: 'Site clearing',
        startDate: '2023-11-20',
        endDate: '2023-11-25',
        color: '#8B4513'
      },
      {
        name: 'Leveling',
        startDate: '2023-11-23',
        endDate: '2023-12-05',
        color: '#8B4513'
      }
    ]
  },
  {
    user: 'John@construction....',
    tasks: [
      {
        name: 'Cleanup',
        startDate: '2023-11-28',
        endDate: '2023-12-03',
        color: '#B8860B'
      },
      {
        name: 'Concrete',
        startDate: '2023-12-01',
        endDate: '2023-12-07',
        color: '#B8860B'
      }
    ]
  }
])

// Add timeline navigation functions
const navigateTimeline = (direction) => {
  const amount = timeView.value === 'days' ? 7 : 
                 timeView.value === 'weeks' ? 4 : 
                 timeView.value === 'months' ? 1 : 1
                 
  const date = new Date(currentDate.value)
  if (direction === 'forward') {
    date.setDate(date.getDate() + amount)
  } else {
    date.setDate(date.getDate() - amount)
  }
  currentDate.value = date
}

const goToToday = () => {
  currentDate.value = new Date()
}

const changeTimeView = (view) => {
  timeView.value = view
}

// Add to script setup section, near other refs
const showTimeline = ref(false)
const showTaskDetails = ref(null)

// Add these helper functions for task positioning
const getTaskPosition = (startDate) => {
  // Convert startDate to a position percentage
  const start = new Date(startDate)
  const timelineStart = new Date('2023-11-16') // First day of Week 47
  const timelineEnd = new Date('2023-12-06')   // Last day of Week 50
  
  const totalDays = (timelineEnd - timelineStart) / (1000 * 60 * 60 * 24)
  const daysSinceStart = (start - timelineStart) / (1000 * 60 * 60 * 24)
  
  return (daysSinceStart / totalDays) * 100
}

const getTaskWidth = (startDate, endDate) => {
  // Calculate width based on duration
  const start = new Date(startDate)
  const end = new Date(endDate)
  const timelineStart = new Date('2023-11-16')
  const timelineEnd = new Date('2023-12-06')
  
  const totalDays = (timelineEnd - timelineStart) / (1000 * 60 * 60 * 24)
  const taskDays = (end - start) / (1000 * 60 * 60 * 24)
  
  return (taskDays / totalDays) * 100
}

// Add a button to show timeline in your header section
const toggleTimeline = () => {
  showTimeline.value = !showTimeline.value
}

// Add to script setup section
import TimelineView from './TimelineView.vue'

// Add current tickets (static data)
const currentTickets = [
  {"id":1,"ticket_number":"TCK001","title":"Competition Splits Filters","description":"Add ability to filter data based on competition splits. This will allow users to analyze data across different competition segments","status":"open","priority":"high","type":"feature","assignee":"Sarah Wilson","reporter_name":"admin","assignee_name":"Sarah Wilson","importance":6,"created_at":"10.12.2024 12:35:16","updated_at":"06.01.2025 14:07:34","folder_id":null,"comments":[{"id":4,"ticket_id":1,"text":"Kas on vaja kautada erinevated hooaegadel erinevate competitison splitti ka v\u00f5i sobib et k\u00f5igil on sama","user":null,"created_at":"2025-01-02T07:33:24.000000Z","updated_at":"2025-01-02T07:33:24.000000Z"},{"id":5,"ticket_id":1,"text":"Ma kommenteerin seda ticketit.\n\nOskar","user":null,"created_at":"2025-01-02T12:30:41.000000Z","updated_at":"2025-01-02T12:30:41.000000Z"}],"tests":[]},
  {"id":2,"ticket_number":"TCK002","title":"Additional Comparable Data Integration","description":"Implement system for displaying and comparing additional data points. Include functionality for data comparison across different metrics.","status":"in_progress","priority":"high","type":"feature","assignee":"David Lee","reporter_name":"admin","assignee_name":"David Lee","importance":5,"created_at":"10.12.2024 12:35:16","updated_at":"10.12.2024 12:35:16","folder_id":null,"comments":[],"tests":[]},
  {"id":3,"ticket_number":"TCK003","title":"Per 30 Templates Implementation","description":"Create templates for per-30 analysis. Include customizable fields and standard metrics for consistent analysis.","status":"open","priority":"medium","type":"feature","assignee":"Sarah Wilson","reporter_name":"admin","assignee_name":"Sarah Wilson","importance":9,"created_at":"10.12.2024 12:35:16","updated_at":"10.12.2024 12:35:16","folder_id":null,"comments":[],"tests":[]},
  // ... (truncated for brevity, add all tickets from your provided array here) ...
]

// On mount, add current tickets to the tickets list if not already present
onMounted(() => {
  // fetchTickets()   // Disabled - no tickets API
  // fetchFolders()   // Disabled - no folders API
  document.addEventListener('mousemove', handleDrag)
  document.addEventListener('mouseup', () => isDragging.value = false)

  // Merge currentTickets into tickets if not already present
  for (const t of currentTickets) {
    if (!tickets.value.find(existing => existing.id === t.id)) {
      tickets.value.push(t)
    }
  }
})

// Helper to parse date string to Date object
function parseTicketDate(dateStr) {
  // Handles both 'DD.MM.YYYY HH:mm:ss' and ISO
  if (!dateStr) return null
  if (dateStr.includes('-') && dateStr.includes('T')) return new Date(dateStr)
  const [d, m, yAndTime] = dateStr.split('.')
  if (!d || !m || !yAndTime) return null
  const [y, time] = [yAndTime.slice(0, 4), yAndTime.slice(5)]
  return new Date(`${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}T${time || '00:00:00'}`)
}

// Add tickets to timelineData for visualization
watch(
  () => tickets.value.length,
  () => {
    // Group tickets by assignee for timeline
    const userMap = {}
    for (const t of tickets.value) {
      const assignee = t.assignee_name || t.assignee || 'Unassigned'
      if (!userMap[assignee]) userMap[assignee] = []
      // Use created_at as start, updated_at as end (or +2 days if missing)
      const start = parseTicketDate(t.created_at)
      const end = parseTicketDate(t.updated_at) || (start ? new Date(start.getTime() + 2 * 24 * 60 * 60 * 1000) : new Date())
      userMap[assignee].push({
        name: t.title,
        startDate: start ? start.toISOString().slice(0, 10) : '',
        endDate: end ? end.toISOString().slice(0, 10) : '',
        color: t.status === 'open' ? '#8B4513' : t.status === 'in_progress' ? '#B8860B' : t.status === 'testing' ? '#4682B4' : '#2E8B57'
      })
    }
    timelineData.value = Object.entries(userMap).map(([user, tasks]) => ({ user, tasks }))
  },
  { immediate: true }
)
</script>

<template>
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-8">
    <div class="bg-white rounded-lg shadow-xl w-full h-full flex overflow-hidden">
      <!-- Users Sidebar -->
      <div class="w-64 border-r flex flex-col shrink-0 bg-gray-50">
        <div class="p-4 border-b">
          <h3 class="text-sm font-medium text-gray-500 uppercase mb-2">Users</h3>
          <!-- All Users option -->
          <button
            @click="selectedUserId = null"
            class="w-full flex items-center p-2 rounded-lg transition-colors mb-4 bg-white border shadow-sm"
            :class="!selectedUserId ? 'bg-pink-50 text-pink-500' : 'text-gray-700 hover:bg-gray-100'"
          >
            <i class="fas fa-users"></i>
            <span class="ml-3 flex-1">All Users</span>
            <span class="bg-gray-200 text-gray-800 px-2 rounded-full text-xs">
              {{ users.length }}
            </span>
          </button>
          
          <!-- Search users -->
          <div class="relative mb-3">
            <input
              v-model="userSearchQuery"
              type="text"
              placeholder="Search users..."
              class="w-full pl-8 pr-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            >
            <i class="fas fa-search absolute left-2.5 top-2.5 text-gray-400 text-sm"></i>
          </div>
        </div>
        
        <!-- Users list -->
        <div class="flex-1 overflow-y-auto p-3">
          <div class="space-y-1">
            <button
              v-for="user in filteredUsers"
              :key="user.id"
              @click="selectedUserId = user.id"
              class="w-full flex items-center p-3 rounded-lg transition-all bg-white border hover:shadow-sm"
              :class="selectedUserId === user.id ? 'bg-pink-50 text-pink-600 border-pink-200 shadow-sm' : 'text-gray-700 hover:bg-gray-50 border-gray-100'"
            >
              <!-- Avatar with integrated online status -->
              <div class="relative">
                <div 
                  class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-base font-medium shrink-0"
                  :class="selectedUserId === user.id ? 'bg-pink-100' : ''"
                >
                  {{ user?.name?.charAt(0)?.toUpperCase() || '?' }}
                </div>
                <div 
                  class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white"
                  :class="{
                    'bg-green-400 ring-2 ring-green-100': user?.isOnline,
                    'bg-gray-300': !user?.isOnline
                  }"
                >
                  <span 
                    v-if="user?.isOnline"
                    class="absolute inset-0 rounded-full animate-ping bg-green-400 opacity-75"
                  ></span>
                </div>
              </div>

              <div class="ml-3 flex-1 text-left min-w-0">
                <div class="flex flex-col">
                  <div class="text-sm font-medium">{{ user?.name || 'Unknown User' }}</div>
                  <div class="text-xs text-gray-500">{{ user?.email || 'No email' }}</div>
                  <span 
                    class="text-[11px] text-gray-500 mt-0.5" 
                    :class="{
                      'text-blue-600': user?.role === 'Developer',
                      'text-purple-600': user?.role === 'Designer',
                      'text-green-600': user?.role === 'QA',
                      'text-amber-600': user?.role === 'Product Manager',
                      'text-gray-600': user?.role === 'Support'
                    }"
                  >
                    {{ user?.role }}
                  </span>
                </div>
              </div>
            </button>
          </div>
        </div>
        
        <!-- User stats -->
        <div class="p-2 border-t bg-white">
          <div class="flex justify-between text-center text-xs text-gray-500">
            <div class="p-2 rounded-lg bg-gray-50">
              <div class="font-medium text-gray-900">{{ users.length }}</div>
              Total
            </div>
            <div class="p-2 rounded-lg bg-gray-50">
              <div class="font-medium text-gray-900">{{ onlineUsersCount }}</div>
              Online
            </div>
          </div>
        </div>
      </div>

      <!-- Left Sidebar (Categories & Folders) -->
      <div class="w-80 border-r flex flex-col shrink-0">
        <!-- New Ticket Button -->
        

        <!-- Categories -->
        <div class="flex-1 overflow-y-auto flex flex-col">
          <div class="p-4">
            <h3 class="text-sm font-medium text-gray-500 uppercase mb-2">Categories</h3>
            <div class="space-y-1">
              <button
                v-for="category in categories"
                :key="category.id"
                @click="activeCategory = category.id"
                class="w-full flex items-center p-2 rounded-lg transition-colors"
                :class="activeCategory === category.id ? 'bg-pink-50 text-pink-500' : 'text-gray-700 hover:bg-gray-100'"
              >
                <i :class="['fas', category.icon]"></i>
                <span class="ml-3">{{ category.label }}</span>
                <span 
                  v-if="category.id === 'all'"
                  class="ml-auto bg-gray-200 text-gray-800 px-2 rounded-full text-sm"
                >
                  {{ tickets.length }}
                </span>
              </button>
            </div>
          </div>

          <!-- Keep existing Folders section -->
          <div class="p-4 border-t">
            <h3 class="text-sm font-medium text-gray-500 uppercase mb-2">Folders</h3>
            <!-- Add search box for folders -->
            <div class="relative mb-3">
              <input
                v-model="folderSearchQuery"
                type="text"
                placeholder="Search folders..."
                class="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
              <i class="fas fa-search absolute left-2.5 top-2.5 text-gray-400 text-sm"></i>
            </div>
            <div class="space-y-1">
              <button
                v-for="folder in filteredFolders"
                :key="folder.id"
                @click="selectedFolder = folder.id"
                class="w-full flex items-center p-2 rounded-lg transition-colors group relative"
                :class="[
                  selectedFolder === folder.id ? 'bg-pink-50 text-pink-500' : 'text-gray-700 hover:bg-gray-100',
                  folder.isPending ? 'opacity-50' : '',
                  failedFolders.has(folder.id) ? 'border-red-300 bg-red-50' : ''
                ]"
              >
                <i :class="['fas', `fa-${folder.icon}`]"></i>
                <div   class="ml-3  text-left flex-1">{{ folder.name }}</div>
                <!-- Quick Add Ticket button -->
                <button
                  v-if="!failedFolders.has(folder.id) && !pendingFolders.has(folder.id)"
                  @click.stop="quickAddTicketToFolder(folder.id, $event)"
                  class="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-pink-500 p-1 mr-1"
                  title="Add ticket to this folder"
                >
                  <i class="fas fa-plus-circle"></i>
                </button>
                <!-- Retry button for failed folders -->
                <button
                  v-if="failedFolders.has(folder.id)"
                  @click.stop="retryCreateFolder(folder.id)"
                  class="px-2 py-1 text-xs bg-red-100 text-red-600 rounded hover:bg-red-200 mr-2"
                >
                  <i class="fas fa-redo-alt mr-1"></i>
                  Retry
                </button>
                <!-- Error Log button -->
                <button
                  v-if="failedFolders.has(folder.id)"
                  @click.stop="showFolderErrorLog(folder.id, $event)"
                  class="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                  title="View error details"
                >
                  <i class="fas fa-clipboard-list"></i>
                </button>
                <!-- Show trash icon only for successfully created folders -->
                <div 
                  v-if="!failedFolders.has(folder.id) && !pendingFolders.has(folder.id)"
                  class="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <button
                    v-if="folder.id !== 1"
                    @click.stop="confirmDelete(folder.id, $event)"
                    class="text-gray-400 hover:text-red-500 p-1"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </div>
              </button>
            </div>
            <button
              @click="showNewFolderForm = true"
              class="w-full mt-2 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2"
            >
              <i class="fas fa-plus"></i>
              <span>New Folder</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Ticket List with Enhanced Layout -->
      <!-- <div 
        class="flex-shrink-0 flex flex-col border-r overflow-x-auto"
        :style="{ width: `${ticketListWidth}px` }"
      > -->
      <div class="w-[2500px] flex flex-col border-r overflow-x-auto">

      <div class="flex items-center justify-between p-4 bg-gray-100 border-b">
        <!-- Search Bar -->
        <div class="flex-1 mr-4">
          <div class="relative">
            <input 
              v-model="searchQuery"
              type="text"
              placeholder="Search tickets..."
              class="w-full pl-10 pr-4 py-2 bg-white border border-pink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            >
            <i class="fas fa-search absolute left-3 top-2.5 text-pink-500"></i>
          </div>
        </div>
        <!-- New Ticket Button -->
        <button 
          @click="showNewTicketForm = true"
          class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 flex items-center justify-center gap-2 whitespace-nowrap"
        >
          <i class="fas fa-plus"></i>
          <span>New Ticket</span>
        </button>
        <!-- Add this button next to New Ticket button in header -->
        <button 
          @click="showTimeline = true"
          class="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-2 mr-2"
        >
          <i class="fas fa-calendar-alt"></i>
          <span>Timeline</span>
        </button>
      </div>

        <!-- Header with Filters -->
        <div class="p-4 border-b bg-white space-y-3">
          <h2 class="text-2xl font-semibold text-gray-800 flex items-center gap-3">
            <i :class="['fas', categories.find(c => c.id === activeCategory)?.icon, 'text-gray-400']"></i>
            {{ categories.find(c => c.id === activeCategory)?.label }}
            <span class="text-sm font-normal text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {{ filteredTickets.length }} tickets
            </span>
          </h2>
          
          <!-- Compact Sort Toolbar -->
          <div class="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg border">
            <button
              v-for="(label, value) in sortOptions"
              :key="value"
              @click="updateSort(value)"
              class="px-2 py-1 rounded-md text-xs transition-all flex items-center gap-1 whitespace-nowrap"
              :class="sortBy === value ? 'bg-white text-pink-500 shadow-sm font-medium' : 'text-gray-600 hover:bg-gray-100'"
            >
              <i :class="[
                'fas',
                value === 'newest' ? (sortDirection === 'asc' ? 'fa-arrow-up-short-wide' : 'fa-arrow-down-short-wide') :
                value === 'oldest' ? (sortDirection === 'asc' ? 'fa-arrow-down-short-wide' : 'fa-arrow-up-short-wide') :
                value === 'priority' ? (sortDirection === 'asc' ? 'fa-arrow-up-wide-short' : 'fa-arrow-down-wide-short') :
                value === 'importance' ? (sortDirection === 'asc' ? 'fa-arrow-up-1-9' : 'fa-arrow-down-9-1') :
                'fa-sort'
              ]"></i>
              {{ label }}
              <i v-if="sortBy === value" :class="[
                'fas fa-xs',
                sortDirection === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'
              ]"></i>
            </button>
          </div>
        </div>

        <!-- Update the timeline modal wrapper -->
        <div v-if="showTimeline" class="border-b">
          <!-- Timeline Modal -->
          <div class="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-8">
            <div class="bg-white rounded-xl shadow-2xl w-[95vw] h-[90vh] flex flex-col overflow-hidden">
              <!-- Timeline Header -->
              <div class="flex items-center justify-between p-4 border-b bg-white">
                <div class="flex items-center gap-6">
                  <h2 class="text-xl font-semibold text-gray-800">Timeline View</h2>
                  
                  <div class="flex items-center gap-2 bg-gray-50 p-1 rounded-lg">
                    <button 
                      v-for="view in ['days', 'weeks', 'months', 'years']"
                      :key="view"
                      @click="changeTimeView(view)"
                      class="px-4 py-2 text-sm rounded-lg transition-all"
                      :class="timeView === view ? 'bg-white text-pink-600 shadow-sm font-medium' : 'text-gray-600 hover:bg-gray-100'"
                    >
                      {{ view.charAt(0).toUpperCase() + view.slice(1) }}
                    </button>
                  </div>

                  <div class="flex items-center gap-2">
                    <button @click="navigateTimeline('back')" class="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100">
                      <i class="fas fa-chevron-left"></i>
                    </button>
                    <button @click="goToToday" class="px-4 py-2 text-sm text-pink-600 bg-pink-50 rounded-lg hover:bg-pink-100">
                      Today
                    </button>
                    <button @click="navigateTimeline('forward')" class="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100">
                      <i class="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>

                <button @click="showTimeline = false" class="text-gray-500 hover:text-gray-700 p-2">
                  <i class="fas fa-times"></i>
                </button>
              </div>

              <!-- Timeline Content -->
              <div class="flex-1 overflow-hidden">
                <div class="overflow-x-auto h-full">
                  <div class="min-w-max p-6">
                    <!-- Weeks Row -->
                    <div class="flex">
                      <div class="w-60 shrink-0"><!-- User column --></div>
                      <div class="flex">
                        <div v-for="week in ['Week 47', 'Week 48', 'Week 49', 'Week 50']" 
                             :key="week"
                             class="w-60 px-3">
                          <div class="text-sm font-medium text-gray-800">{{ week }}</div>
                          <div class="text-xs text-gray-500 mt-1">Nov 16 - Nov 22</div>
                          
                          <!-- Days Grid -->
                          <div class="grid grid-cols-7 gap-1 mt-2">
                            <div v-for="day in 7" 
                                 :key="day"
                                 class="w-6 h-6 flex items-center justify-center text-xs rounded-full"
                                 :class="day === 3 ? 'bg-pink-500 text-white' : 'text-gray-600'">
                              {{ day }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Tasks Grid -->
                    <div class="mt-8">
                      <div v-for="userData in timelineData" 
                           :key="userData.user" 
                           class="flex items-start mb-6 relative">
                        <!-- User Info -->
                        <div class="w-60 shrink-0 flex items-center gap-3 pr-4">
                          <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium">
                            {{ userData.user.charAt(0) }}
                          </div>
                          <div class="flex flex-col">
                            <span class="text-sm font-medium text-gray-800">{{ userData.user }}</span>
                            <span class="text-xs text-gray-500">5 tasks</span>
                          </div>
                        </div>
                        
                        <!-- Tasks Timeline -->
                        <div class="relative flex-1 h-16">
                          <div v-for="task in userData.tasks" 
                               :key="task.name"
                               class="absolute h-12 rounded-lg text-white px-3 py-2 text-sm cursor-pointer transition-all hover:shadow-lg"
                               :style="{
                                 left: `${getTaskPosition(task.startDate)}%`,
                                 width: `${getTaskWidth(task.startDate, task.endDate)}%`,
                                 backgroundColor: task.color
                               }"
                               @mouseenter="showTaskDetails = task"
                               @mouseleave="showTaskDetails = null"
                          >
                            {{ task.name }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tickets Grid/List -->
        <div class="flex-1 overflow-y-auto p-4">
          <div 
            :class="[
              viewMode === 'grid' ? 'grid grid-cols-2 gap-4' : 'space-y-4',
              'auto-rows-max'
            ]"
          >
            <div 
              v-for="ticket in sortedAndFilteredTickets"
              :key="ticket.id"
              @click="selectedTicket = ticket"
              class="group border rounded-lg hover:shadow-lg transition-all duration-200 cursor-pointer"
              :class="[
                selectedTicket?.id === ticket.id ? 'border-pink-500 bg-pink-50' : 'border-gray-200',
                viewMode === 'grid' ? 'p-6' : 'p-4'
              ]"
            >
              <!-- Ticket Header -->
              <div class="flex items-start justify-between mb-3">
                <div>
                  <div class="flex items-center gap-2">
                    <span 
                      class="text-sm font-mono bg-gray-100 px-2 py-1 rounded"
                      :class="selectedTicket?.id === ticket.id ? 'bg-pink-100' : ''"
                    >
                      {{ ticket.id }}
                    </span>
                    <h3 class="font-medium text-lg">{{ ticket.title }}</h3>
                  </div>
                  <p class="text-sm text-gray-600 mt-2 line-clamp-2">{{ ticket.description }}</p>
                </div>
                <div class="flex flex-col items-end gap-2">
               
                  <span 
                    class="px-2 py-1 text-xs rounded-full"
                    :class="typeBadges[ticket.type]"
                  >
                    {{ ticket.type }}
                  </span>
                </div>
              </div>

              <!-- Add this inside the ticket card, after the ticket description -->
              <div class="flex items-center gap-2 mt-3" v-if="ticket.assignee">
                <div class="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium">
                  {{ ticket.assignee.avatar }}
                </div>
                <div class="flex flex-col">
                  <span class="text-sm">{{ ticket.assignee.name }}</span>
                  <span class="text-xs text-gray-500">{{ ticket.assignee.role }}</span>
                </div>
              </div>

              <!-- Add this after the ticket header div and before ticket details -->
              <div class="flex items-center gap-2 mb-3">
                <span class="text-xs text-gray-500">Importance:</span>
                <div class="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    class="h-full transition-all duration-300 bg-gradient-to-r from-pink-500 to-pink-400"
                    :style="{
                      width: `${(ticket.importance || 0) * 10}%`,
                      opacity: `${((ticket.importance || 0) * 0.2) + 0.3}`
                    }"
                  ></div>
                </div>
                <span class="text-xs font-medium">
                  <span :class="importanceColors[ticket.importance]">
                    {{ ticket.importance || 0 }}
                  </span>
                  /10
                </span>
              </div>

              <!-- Add to the ticket card, after priority and type badges -->
              <div class="flex flex-wrap gap-1 mt-2">
                <span
                  v-for="tag in ticket.tags"
                  :key="tag.id"
                  class="px-2 py-1 text-xs rounded-full"
                  :class="`bg-${tag.color}-100 text-${tag.color}-800`"
                >
                  {{ tag.name }}
                </span>
              </div>

              <!-- Ticket Details -->
              <div class="flex items-center justify-between text-sm text-gray-500">
                <div class="flex items-center gap-4">
                  <div class="flex items-center gap-2">
                    <div class="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                      {{ ticket.reporter?.name?.charAt(0) || 'U' }}
                    </div>
                    <span>{{ ticket.reporter?.name || 'Unknown' }}</span>
                  </div>
                  <div class="flex items-center gap-1">
                    <i class="fas fa-clock"></i>
                    <span>{{ ticket.created_at }}</span>
                  </div>
                </div>
                <div class="flex items-center gap-4">
                  <div class="flex items-center gap-1">
                    <i class="fas fa-comments"></i>
                    <span>{{ ticket.comments?.length }}</span>
                  </div>
                  <div 
                    class="opacity-0 group-hover:opacity-100 transition-opacity"
                    v-if="ticket.status !== 'closed'"
                  >
                    <button 
                      class="text-pink-500 hover:text-pink-600"
                      @click.stop="showTicketActions(ticket)"
                    >
                      <i class="fas fa-ellipsis-v"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Progress Bar for Testing Status -->
              <div 
                v-if="ticket.status === 'testing'"
                class="mt-3 bg-gray-100 rounded-full h-2 overflow-hidden"
              >
                <div 
                  class="bg-green-500 h-full transition-all duration-500"
                  :style="{
                    width: `${getTestingProgress(ticket.id)}%`
                  }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Add the draggable divider -->
      <div
        class="w-1 hover:w-2 bg-gray-200 hover:bg-pink-500 cursor-col-resize transition-all duration-150 active:bg-pink-600 flex-shrink-0"
        @mousedown="isDragging = true"
        @dblclick="ticketListWidth = 1500"
      ></div>

      <!-- Ticket Details -->
      <div class="w-full flex flex-col flex-shrink">
        <!-- Tab Navigation -->
        <div class="flex border-b">
          <button 
            @click="showChat = false"
            class="flex-1 px-4 py-3 text-center font-medium transition-colors"
            :class="!showChat ? 'border-b-2 border-pink-500 text-pink-500' : 'text-gray-500 hover:text-gray-700'"
          >
            Details
          </button>
          <button 
            @click="showChat = true"
            class="flex-1 px-4 py-3 text-center font-medium transition-colors"
            :class="showChat ? 'border-b-2 border-pink-500 text-pink-500' : 'text-gray-500 hover:text-gray-700'"
          >
            Chat
          </button>
          <button 
            @click="$emit('close')"
            class="px-4 py-3 text-gray-500 hover:text-gray-700"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Details View -->
        <div v-if="!showChat" class="flex-1 overflow-y-auto">
          <div class="p-4 border-b flex items-center justify-between">
            <h2 class="text-xl font-semibold">Details</h2>
            <div class="flex items-center gap-2">
              <!-- Modify the edit button to call saveTicket instead of toggling editMode directly -->
              <button 
                v-if="selectedTicket"
                @click="handleSaveChanges"
                class="px-3 py-1.5 rounded-lg text-sm flex items-center gap-2"
                :class="editMode ? 'bg-pink-100 text-pink-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
              >
                <i class="fas" :class="editMode ? 'fa-check' : 'fa-edit'"></i>
                {{ editMode ? 'Save Changes' : 'Edit Ticket' }}
              </button>
              <button 
                @click="$emit('close')"
                class="text-gray-500 hover:text-gray-700"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <div v-if="selectedTicket" class="flex-1 overflow-y-auto p-4">
            <div class="space-y-6">
              <!-- Ticket Info -->
              <div>
                <div v-if="editMode" class="space-y-3">
                  <input 
                    v-model="selectedTicket.title"
                    class="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 font-medium"
                    placeholder="Ticket title"
                  >
                  <textarea
                    v-model="selectedTicket.description"
                    rows="4"
                    class="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="Ticket description"
                  ></textarea>
                </div>
                <div v-else>
                  <h3 class="font-medium mb-2">{{ selectedTicket.title }}</h3>
                  <p class="text-gray-600">{{ selectedTicket.description }}</p>
                </div>
              </div>

              <!-- Metadata -->
              <div class="space-y-2">
                <div class="flex items-center justify-between">
                  <span class="text-gray-500">Status</span>
                  <select
                    v-if="editMode"
                    v-model="selectedTicket.status"
                    class="px-2 py-1 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="testing">Testing</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                  <span v-else class="px-2 py-1 text-sm rounded-full" :class="statusBadges[selectedTicket.status]">
                    {{ selectedTicket.status }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-gray-500">Priority</span>
                  <select
                    v-if="editMode"
                    v-model="selectedTicket.priority"
                    class="px-2 py-1 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                  <span v-else class="px-2 py-1 text-sm rounded-full" :class="priorityBadges[selectedTicket.priority]">
                    {{ selectedTicket.priority }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-gray-500">Type</span>
                  <select
                    v-if="editMode"
                    v-model="selectedTicket.type"
                    class="px-2 py-1 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="bug">Bug</option>
                    <option value="feature">Feature</option>
                    <option value="improvement">Improvement</option>
                    <option value="support">Support</option>
                  </select>
                  <span v-else class="px-2 py-1 text-sm rounded-full" :class="typeBadges[selectedTicket.type]">
                    {{ selectedTicket.type }}
                  </span>
                </div>

                <!-- Add this after the Type selector in the ticket details section -->
                <div class="flex items-center flex-col justify-between">
                  <span class="text-gray-500">Importance</span>
                  <div class="flex flex-wrap gap-1 w-full mt-2">
                    <button
                      v-for="n in 10"
                      :key="n"
                      @click="editMode ? updateImportance(n) : null"
                      class="flex-1 py-1.5 rounded-lg transition-all text-sm relative"
                      :class="[
                        selectedTicket.importance === n 
                          ? `${importanceColors[n]} bg-pink-100 border-pink-300 font-medium shadow-md ring-2 ring-pink-300 scale-110 z-[1]` 
                          : [
                            'text-gray-700 bg-gray-200',
                            editMode ? 'hover:bg-gray-300 hover:text-gray-900 hover:shadow-md hover:scale-105 cursor-pointer' : 'cursor-default'
                          ]
                      ]"
                    >
                      {{ n }}
                      <div 
                        v-if="selectedTicket.importance === n"
                        class="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-pink-600 rounded-full"
                      ></div>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Testing Section -->
              <div v-if="selectedTicket.status === 'testing' || selectedTicket.status === 'resolved'">
                <div class="flex items-center justify-between mb-4">
                  <h4 class="font-medium">Test Results</h4>
                  <button 
                    v-if="selectedTicket.status === 'testing'"
                    @click="showNewTestForm = true"
                    class="text-sm px-3 py-1 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
                  >
                    Add Test Result
                  </button>
                </div>
                
                <div class="space-y-3">
                  <div 
                    v-for="testResult in testResults.find(t => t.ticketId === selectedTicket.id)?.tests"
                    :key="testResult.id"
                    class="p-3 rounded-lg border"
                    :class="{
                      'border-green-200 bg-green-50': testResult.status === 'passed',
                      'border-red-200 bg-red-50': testResult.status === 'failed',
                      'border-yellow-200 bg-yellow-50': testResult.status === 'pending'
                    }"
                  >
                    <div class="flex items-center justify-between mb-2">
                      <span class="font-medium">{{ testResult.name }}</span>
                      <span 
                        class="px-2 py-1 text-xs rounded-full"
                        :class="{
                          'bg-green-100 text-green-800': testResult.status === 'passed',
                          'bg-red-100 text-red-800': testResult.status === 'failed',
                          'bg-yellow-100 text-yellow-800': testResult.status === 'pending'
                        }"
                      >
                        {{ testResult.status }}
                      </span>
                    </div>
                    <p class="text-sm text-gray-600">{{ testResult.notes }}</p>
                    <div class="text-xs text-gray-500 mt-2">
                      {{ new Date(testResult.timestamp).toLocaleString() }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Testing Actions -->
              <div 
                v-if="selectedTicket.status !== 'testing' && selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed'"
                class="border-t pt-4"
              >
                <button 
                  @click="moveToTesting(selectedTicket)"
                  class="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2"
                >
                  <i class="fas fa-vial"></i>
                  <span>Move to Testing</span>
                </button>
              </div>

              <!-- Comments -->
              <div>
                <h4 class="font-medium mb-2">Comments</h4>
                <div class="space-y-4">
                  <div 
                    v-for="comment in selectedTicket.comments"
                    :key="comment.id"
                    class="p-3 bg-gray-50 rounded-lg"
                  >
                    <div class="flex items-center justify-between mb-1">
                      <span class="font-medium">{{ comment.user || 'Anonymous' }}</span>
                      <span class="text-sm text-gray-500">{{ comment.time }}</span>
                    </div>
                    <p class="text-gray-600">{{ comment.text }}</p>
                  </div>
                </div>

                <!-- New Comment -->
                <div class="mt-4">
                  <textarea
                    v-model="newComment"
                    placeholder="Add a comment..."
                    rows="3"
                    class="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  ></textarea>
                  <button 
                    @click="addComment"
                    class="mt-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
                  >
                    Add Comment
                  </button>
                </div>
              </div>

              <!-- Add to the ticket details section -->
              <div class="space-y-2 mt-4">
                <div class="flex items-center justify-between">
                  <span class="text-gray-500">Folder</span>
                  <div class="relative">
                    <div
                      @click="showFolderSelect = !showFolderSelect"
                      class="w-full p-2 border rounded-lg flex items-center justify-between cursor-pointer hover:bg-gray-50"
                      :class="showFolderSelect ? 'ring-2 ring-pink-500 border-transparent' : 'border-gray-200'"
                    >
                      <div class="flex items-center gap-2">
                        <i v-if="selectedFolder" :class="['fas', `fa-${filteredFolders.find(f => f.id === newTicket.folder)?.icon || 'folder'}`]"></i>
                        <span class="text-gray-700">
                          {{ folders.find(f => f.id === newTicket.folder)?.name || 'No Folder' }}
                        </span>
                      </div>
                      <i class="fas fa-chevron-down text-gray-400" :class="{ 'transform rotate-180': showFolderSelect }"></i>
                    </div>
                    
                    <!-- Dropdown Menu -->
                    <div
                      v-if="showFolderSelect"
                      class="absolute z-10 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-60 overflow-y-auto"
                    >
                      <div
                        @click="newTicket.folder = ''; showFolderSelect = false"
                        class="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                      >
                        <i class="fas fa-folder-minus text-gray-400"></i>
                        <span>No Folder</span>
                      </div>
                      <div
                        v-for="folder in filteredFolders"
                        :key="folder.id"
                        @click="newTicket.folder = folder.id; showFolderSelect = false"
                        class="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                        :class="{ 'bg-pink-50': newTicket.folder === folder.id }"
                      >
                        <i :class="['fas', `fa-${folder.icon}`, newTicket.folder === folder.id ? 'text-pink-500' : 'text-gray-400']"></i>
                        <span :class="{ 'text-pink-500': newTicket.folder === folder.id }">{{ folder.name }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="flex items-center justify-between">
                  <span class="text-gray-500">Tags</span>
                  <button
                    @click="showTagsModal = true"
                    class="text-sm px-3 py-1 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Manage Tags
                  </button>
                </div>
                
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="tag in selectedTicket.tags"
                    :key="tag.id"
                    class="px-2 py-1 text-xs rounded-full flex items-center gap-1"
                    :class="`bg-${tag.color}-100 text-${tag.color}-800`"
                  >
                    {{ tag.name }}
                    <button
                      @click="removeTag(tag.id)"
                      class="hover:text-red-500"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="flex-1 flex items-center justify-center text-gray-500">
            Select a ticket to view details
          </div>
        </div>

        <!-- Chat View -->
        <div v-else class="flex-1 flex flex-col">
          <!-- Chat Header -->
          <div class="p-4 border-b">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="font-medium">Support Chat</h3>
                <p class="text-sm text-gray-500">
                  {{ participants.filter(p => p.status === 'online').length }} agents online
                </p>
              </div>
              <div class="flex items-center gap-2">
                <div 
                  v-for="participant in participants"
                  :key="participant.id"
                  class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center relative"
                >
                  <span class="text-sm font-medium">
                    {{ participant.name.charAt(0) }}
                  </span>
                  <div 
                    class="absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white"
                    :class="participant.status === 'online' ? 'bg-green-500' : 'bg-gray-400'"
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Chat Messages -->
          <div class="flex-1 overflow-y-auto p-4">
            <div class="space-y-4">
              <div 
                v-for="message in chatMessages"
                :key="message.id"
                class="flex flex-col"
                :class="message.type === 'user' ? 'items-end' : 'items-start'"
              >
                <div 
                  class="max-w-[80%] rounded-lg p-3"
                  :class="message.type === 'user' ? 'bg-pink-500 text-white' : 'bg-gray-100'"
                >
                  <div class="text-xs mb-1" :class="message.type === 'user' ? 'text-pink-100' : 'text-gray-500'">
                    {{ message.sender }}
                  </div>
                  <div>{{ message.text }}</div>
                  <div class="text-xs mt-1" :class="message.type === 'user' ? 'text-pink-100' : 'text-gray-500'">
                    {{ new Date(message.time).toLocaleTimeString() }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Chat Input -->
          <div class="p-4 border-t">
            <form @submit.prevent="sendChatMessage" class="flex gap-2">
              <input
                v-model="chatMessage"
                type="text"
                placeholder="Type your message..."
                class="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                @keydown="handleChatKeydown"
              >
              <button
                type="submit"
                class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 flex items-center gap-2"
                :disabled="!chatMessage.trim()"
              >
                <span>Send</span>
                <i class="fas fa-paper-plane"></i>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- New Ticket Modal -->
    <div v-if="showNewTicketForm" class="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-xl font-semibold">Create New Ticket</h2>
          <button 
            @click="showNewTicketForm = false"
            class="text-gray-500 hover:text-gray-700"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input 
              v-model="newTicket.title"
              type="text"
              class="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="Enter ticket title"
            >
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              v-model="newTicket.description"
              rows="4"
              class="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="Enter ticket description"
            ></textarea>
          </div>

          <!-- Selected Folder -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Folder</label>
            <div class="relative">
              <div
                @click="showFolderSelect = !showFolderSelect"
                class="w-full z-[60] p-2 border rounded-lg flex items-center justify-between cursor-pointer hover:bg-gray-50"
                :class="showFolderSelect ? 'ring-2 ring-pink-500 border-transparent' : 'border-gray-200'"
              >
                <div class="flex items-center gap-2">
                  <i v-if="selectedFolder" :class="['fas', `fa-${folders.find(f => f.id === newTicket.folder)?.icon || 'folder'}`]"></i>
                  <span class="text-gray-700">
                    {{ folders.find(f => f.id === newTicket.folder)?.name || 'No Folder' }}
                  </span>
                </div>
                <i class="fas fa-chevron-down text-gray-400" :class="{ 'transform rotate-180': showFolderSelect }"></i>
              </div>
              
              <!-- Dropdown Menu -->
              <div
                v-if="showFolderSelect"
                class="absolute z-10 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-60 overflow-y-auto"
              >
                <div
                  @click="newTicket.folder = ''; showFolderSelect = false"
                  class="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                >
                  <i class="fas fa-folder-minus text-gray-400"></i>
                  <span>No Folder</span>
                </div>
                <div
                  v-for="folder in folders"
                  :key="folder.id"
                  @click="newTicket.folder = folder.id; showFolderSelect = false"
                  class="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                  :class="{ 'bg-pink-50': newTicket.folder === folder.id }"
                >
                  <i :class="['fas', `fa-${folder.icon}`, newTicket.folder === folder.id ? 'text-pink-500' : 'text-gray-400']"></i>
                  <span :class="{ 'text-pink-500': newTicket.folder === folder.id }">{{ folder.name }}</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <div class="flex gap-2">
              <button
                v-for="type in ['bug', 'feature', 'improvement', 'support']"
                :key="type"
                @click="newTicket.type = type"
                class="flex-1 py-2 px-3 rounded-lg border transition-colors"
                :class="[
                  newTicket.type === type 
                    ? `${typeBadges[type]} border-transparent` 
                    : 'border-gray-200 hover:bg-gray-50'
                ]"
              >
                {{ type.charAt(0).toUpperCase() + type.slice(1) }}
              </button>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Importance (1-10)</label>
            <div class="flex gap-2">
              <button
                v-for="n in 10"
                :key="n"
                @click="newTicket.importance = n"
                class="flex-1 py-2 px-1 rounded-lg transition-all text-sm relative"
                :class="[
                  newTicket.importance === n 
                    ? `${importanceColors[n]} bg-pink-50 border-pink-200 font-medium shadow-sm ring-2 ring-pink-200 scale-110 z-[1]` 
                    : [
                      'text-gray-500 bg-gray-100/30',
                      'hover:bg-gray-100 hover:text-gray-700',
                      'hover:shadow-sm hover:scale-105 transition-all duration-150'
                    ]
                ]"
              >
                {{ n }}
                <div 
                  v-if="newTicket.importance === n"
                  class="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-pink-500 rounded-full"
                ></div>
              </button>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
            <div class="relative">
              <div
                @click="showAssigneeSelect = !showAssigneeSelect"
                class="w-full p-2 border rounded-lg flex items-center justify-between cursor-pointer hover:bg-gray-50"
                :class="showAssigneeSelect ? 'ring-2 ring-pink-500 border-transparent' : 'border-gray-200'"
              >
                <div class="flex items-center gap-2">
                  <div v-if="newTicket.assignee" class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium">
                    {{ assignees.find(a => a.id === newTicket.assignee)?.avatar }}
                  </div>
                  <div class="flex flex-col">
                    <span class="text-gray-700">
                      {{ assignees.find(a => a.id === newTicket.assignee)?.name || 'Select Assignee' }}
                    </span>
                    <span class="text-xs text-gray-500" v-if="newTicket.assignee">
                      {{ assignees.find(a => a.id === newTicket.assignee)?.role }}
                    </span>
                  </div>
                </div>
                <i class="fas fa-chevron-down text-gray-400" :class="{ 'transform rotate-180': showAssigneeSelect }"></i>
              </div>
              
              <!-- Dropdown Menu -->
              <div
                v-if="showAssigneeSelect"
                class="absolute z-10 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-60 overflow-y-auto"
              >
                <div class="px-3 py-2 border-b">
                  <input
                    v-model="userSearchQuery"
                    type="text"
                    class="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="Search assignees..."
                  >
                </div>
                <div
                  v-for="assignee in filteredAssignees"
                  :key="assignee.id"
                  @click="selectAssignee(assignee)"
                  class="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                  :class="{ 'bg-pink-50': newTicket.assignee === assignee.id }"
                >
                  <div class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium">
                    {{ assignee.avatar }}
                  </div>
                  <div class="flex flex-col">
                    <span :class="{ 'text-pink-500': newTicket.assignee === assignee.id }">
                      {{ assignee.name }}
                    </span>
                    <span class="text-xs text-gray-500">{{ assignee.role }}</span>
                  </div>
                  <div 
                    class="ml-auto w-2 h-2 rounded-full"
                    :class="{
                      'bg-green-500': assignee.status === 'available',
                      'bg-yellow-500': assignee.status === 'busy',
                      'bg-gray-400': assignee.status === 'offline'
                    }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6 flex justify-end gap-2">
          <button 
            @click="showNewTicketForm = false"
            class="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button 
            @click="createTicket"
            class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
          >
            Create Ticket
          </button>
        </div>
      </div>
    </div>

    <!-- Add New Test Result Modal -->
    <div 
      v-if="showNewTestForm"
      class="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold">Add Test Result</h3>
          <button 
            @click="showNewTestForm = false"
            class="text-gray-500 hover:text-gray-700"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Test Name</label>
            <input 
              v-model="newTest.name"
              type="text"
              class="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="e.g., API Integration Test"
            >
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select 
              v-model="newTest.status"
              class="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            >
              <option value="passed">Passed</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              v-model="newTest.notes"
              rows="3"
              class="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="Add test details and observations..."
            ></textarea>
          </div>
        </div>

        <div class="mt-6 flex justify-end gap-2">
          <button 
            @click="showNewTestForm = false"
            class="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button 
            @click="submitTestResult"
            class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
          >
            Add Result
          </button>
        </div>
      </div>
    </div>

    <!-- Display Loading Indicator -->
    <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
      <div class="loader"></div> <!-- You can style this loader as needed -->
    </div>

    <!-- Display Error Message -->
    <div v-if="error" class="absolute top-4 left-1/2 transform -translate-x-1/2 bg-red-100 text-red-700 px-4 py-2 rounded">
      {{ error }}
      <button @click="error = null" class="ml-2 text-red-500 hover:underline">Dismiss</button>
    </div>

    <!-- Add Tags Modal -->
    <div
      v-if="showTagsModal"
      class="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold">Manage Tags</h3>
          <button
            @click="showTagsModal = false"
            class="text-gray-500 hover:text-gray-700"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="space-y-2">
          <button
            v-for="tag in tags"
            :key="tag.id"
            @click="addTag(tag.id)"
            class="w-full p-2 rounded-lg flex items-center justify-between"
            :class="`hover:bg-${tag.color}-50`"
          >
            <span
              class="px-2 py-1 rounded-full text-sm"
              :class="`bg-${tag.color}-100 text-${tag.color}-800`"
            >
              {{ tag.name }}
            </span>
            <i
              v-if="selectedTicket.tags?.find(t => t.id === tag.id)"
              class="fas fa-check text-green-500"
            ></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Add New Folder Modal -->
    <div
      v-if="showNewFolderForm"
      class="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold">Create New Folder</h3>
          <button
            @click="showNewFolderForm = false"
            class="text-gray-500 hover:text-gray-700"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              v-model="newFolder.name"
              type="text"
              class="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="Enter folder name"
            >
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              v-model="newFolder.description"
              rows="2"
              class="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="Enter folder description (optional)"
            ></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Icon</label>
            <div class="grid grid-cols-5 gap-2">
              <button
                v-for="icon in availableFolderIcons"
                :key="icon"
                @click="newFolder.icon = icon"
                class="p-2 rounded-lg flex items-center justify-center"
                :class="newFolder.icon === icon ? 'bg-pink-50 text-pink-500' : 'hover:bg-gray-100'"
              >
                <i :class="['fas', `fa-${icon}`, 'text-lg']"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="mt-6 flex justify-end gap-2">
          <button
            @click="showNewFolderForm = false"
            class="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            @click="createFolder"
            class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
            :disabled="!newFolder.name"
          >
            Create Folder
          </button>
        </div>
      </div>
    </div>

    <!-- Error Log Modal -->
    <div
      v-if="showErrorLog && selectedErrorLog"
      class="fixed z-[80] bg-white rounded-lg shadow-xl w-[500px] max-h-[80vh] overflow-y-auto"
      :style="{
        left: `${errorLogPosition.x}px`,
        top: `${errorLogPosition.y}px`,
        transform: 'translateY(-50%)'
      }"
    >
      <div class="p-4">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold">Error Log</h3>
          <button
            @click="showErrorLog = false"
            class="text-gray-500 hover:text-gray-700"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="space-y-4">
          <div>
            <h4 class="text-sm font-medium text-gray-700">Timestamp</h4>
            <p class="text-sm text-gray-600">
              {{ new Date(selectedErrorLog.timestamp).toLocaleString() }}
            </p>
          </div>

          <div>
            <h4 class="text-sm font-medium text-gray-700">Error Message</h4>
            <p class="text-sm text-red-600 bg-red-50 p-2 rounded">
              {{ selectedErrorLog.error }}
            </p>
          </div>

          <div>
            <h4 class="text-sm font-medium text-gray-700">Full Error Details</h4>
            <div class="bg-red-50 p-2 rounded">
              <div class="flex items-center gap-2 mb-2">
                <span class="text-sm font-medium text-red-700">
                  {{ selectedErrorLog.fullError.status }} - {{ selectedErrorLog.fullError.name }}
                </span>
                <span class="text-sm text-red-600">
                  {{ selectedErrorLog.fullError.statusText }}
                </span>
              </div>
              
              <div class="space-y-2">
                <div>
                  <h5 class="text-xs font-medium text-gray-700">Error Code:</h5>
                  <pre class="text-xs text-red-600">{{ selectedErrorLog.fullError.code }}</pre>
                </div>

                <div v-if="selectedErrorLog.fullError.data">
                  <h5 class="text-xs font-medium text-gray-700">Response Data:</h5>
                  <pre class="text-xs bg-red-100 p-2 rounded overflow-x-auto">{{ JSON.stringify(selectedErrorLog.fullError.data, null, 2) }}</pre>
                </div>
                
                <div>
                  <h5 class="text-xs font-medium text-gray-700">Request Details:</h5>
                  <pre class="text-xs bg-gray-100 p-2 rounded overflow-x-auto">{{ JSON.stringify(selectedErrorLog.fullError.config, null, 2) }}</pre>
                </div>

                <div>
                  <h5 class="text-xs font-medium text-gray-700">Stack Trace:</h5>
                  <pre class="text-xs bg-gray-100 p-2 rounded overflow-x-auto whitespace-pre-wrap">{{ selectedErrorLog.fullError.stack }}</pre>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 class="text-sm font-medium text-gray-700">Request Details</h4>
            <div class="bg-gray-50 p-2 rounded">
              <p class="text-sm font-mono">
                <span class="text-blue-600">{{ selectedErrorLog.request.method }}</span>
                {{ selectedErrorLog.request.url }}
              </p>
              <pre class="text-xs mt-2 overflow-x-auto">
{{ JSON.stringify(selectedErrorLog.request.data, null, 2) }}
              </pre>
            </div>
          </div>
        </div>

        <div class="mt-6 flex justify-end gap-2">
          <button
            @click="showErrorLog = false"
            class="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Add click outside handler to close popup -->
    <div
      v-if="showErrorLog"
      class="fixed inset-0 z-[79]"
      @click="showErrorLog = false"
    ></div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-lg p-8">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-semibold text-red-600">Delete Folder</h3>
          <button
            @click="showDeleteConfirm = false"
            class="text-gray-500 hover:text-gray-700"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="space-y-6 py-2">
          <p class="text-gray-600">
            This folder will be moved to archive. You can request its restoration from an administrator if needed.
          </p>
          <p class="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
            <i class="fas fa-info-circle mr-1"></i>
            Note: All tickets in this folder will remain accessible in the "All Tickets" view.
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Enter your password to confirm
          </label>
          <input
            v-model="deletePassword"
            type="password"
            class="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            placeholder="Enter your password"
            @keyup.enter="executeDelete"
          >
          <p v-if="deleteError" class="mt-2 text-sm text-red-600">
            {{ deleteError }}
          </p>
        </div>

        <div class="mt-8 flex justify-end gap-3">
          <button
            @click="showDeleteConfirm = false"
            class="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            @click="executeDelete"
            class="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
            :disabled="!deletePassword"
          >
            Delete Folder
          </button>
        </div>
      </div>
    </div>

    <!-- Add timeline component at the end of the template -->
    <TimelineView 
      :show="showTimeline"
      :tickets="tickets"
      @close="showTimeline = false"
    />
  </div>
</template>

<style scoped>
/* Add smooth scrolling for chat messages */
.overflow-y-auto {
  scroll-behavior: smooth;
}

/* Hide scrollbar but keep functionality */
.overflow-y-auto::-webkit-scrollbar {
  width: 0px;
}

/* Optional: Add fade effect for new messages */
.chat-message-enter-active,
.chat-message-leave-active {
  transition: all 0.3s ease;
}

.chat-message-enter-from,
.chat-message-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Add these new styles */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.grid {
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
}

/* Smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

.group:hover {
  transform: translateY(-1px);
}

/* Prevent text selection while dragging */
.cursor-col-resize {
  user-select: none;
}

/* Add smooth transition for width changes */
[style*="width"] {
  transition: width 0.05s ease-out;
}

/* Disable transition during dragging */
:deep(*) {
  user-select: none;
}/* Loader Styles */
.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 2s linear infinite;
}@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes progress {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(200%);
  }
}

.animate-progress {
  animation: progress 1.5s ease-in-out infinite;
}

/* Add styles for popup arrow */
.fixed[style*="transform"] {
  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    border-width: 6px;
    border-style: solid;
    border-color: transparent white transparent transparent;
  }
}

@keyframes ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

.animate-ping {
  animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.overflow-x-auto {
  scrollbar-width: thin;
  scrollbar-color: #E5E7EB transparent;
}

.overflow-x-auto::-webkit-scrollbar {
  height: 6px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background-color: #E5E7EB;
  border-radius: 3px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background-color: #D1D5DB;
}
</style> 




